import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, BookOpen, Clock, CheckCircle2, PlayCircle } from "lucide-react";

export default function StudentCourses() {
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState("");

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [c, e] = await Promise.all([
        api.get("/public/courses"),
        api.get("/student/courses/my-enrollments"),
      ]);
      setCourses(c.data || []);
      setEnrollments(e.data || []);
    } finally { setLoading(false); }
  };

  const enrolledIds = new Set(enrollments.map((e) => e.course_id));

  const enrollFree = async (courseId) => {
    setEnrolling(courseId);
    try {
      await api.post(`/student/courses/${courseId}/enroll`);
      toast.success("Enrolled! Find this course under 'My Courses' below.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrol failed");
    } finally { setEnrolling(""); }
  };

  const payAndEnrol = async (courseId) => {
    setEnrolling(courseId);
    try {
      const { data } = await api.post(`/payments/course-initiate?course_id=${courseId}&return_origin=${encodeURIComponent(window.location.origin)}`);
      if (data.free || data.enrolled || data.already_enrolled) {
        toast.success("Enrolled!");
        load();
        return;
      }
      if (data.payu) {
        toast.info("Redirecting to PayU...");
        const form = document.createElement("form");
        form.method = "POST";
        form.action = data.payu.action;
        for (const [k, v] of Object.entries(data.payu)) {
          const inp = document.createElement("input");
          inp.type = "hidden"; inp.name = k; inp.value = v;
          form.appendChild(inp);
        }
        document.body.appendChild(form);
        form.submit();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment init failed");
    } finally { setEnrolling(""); }
  };

  return (
    <DashboardLayout>
      <h1 className="heading text-2xl font-bold flex items-center gap-2" data-testid="courses-heading">
        <BookOpen className="text-indigo-500" size={26} /> Courses
      </h1>
      <p className="text-slate-500 text-sm mt-1">Skill-building courses curated by PMR Welfare Foundation — videos, notes, and certificates.</p>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="animate-spin mx-auto" /></div>
      ) : (
        <>
          {/* Enrolled (My Courses) */}
          {enrollments.length > 0 && (
            <div className="mt-6">
              <h2 className="heading text-lg font-bold flex items-center gap-1.5">
                <CheckCircle2 size={18} className="text-emerald-500" /> My Courses
              </h2>
              <div className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {enrollments.map((e) => (
                  <Link to={`/student/courses/${e.course_id}`} key={e.id} className="block p-4 rounded-xl border border-emerald-200 bg-emerald-50/30 hover:bg-emerald-50 transition" data-testid={`my-course-${e.course_id}`}>
                    {e.course_thumbnail_url && <img src={e.course_thumbnail_url} alt={e.course_title} className="w-full h-28 object-cover rounded-lg mb-2" />}
                    <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-bold flex items-center gap-1.5">
                      <Clock size={10} /> {e.course_duration || "Self-paced"}
                    </div>
                    <h3 className="heading text-base font-bold mt-1">{e.course_title}</h3>
                    <div className="mt-2 text-xs text-emerald-700 inline-flex items-center gap-1 font-semibold">
                      <PlayCircle size={12} /> Continue learning
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Browse / All Active courses */}
          <div className="mt-8">
            <h2 className="heading text-lg font-bold">Available Courses</h2>
            {courses.length === 0 ? (
              <div className="mt-3 p-12 rounded-xl border border-dashed border-slate-300 text-center">
                <BookOpen size={36} className="mx-auto text-slate-300" />
                <div className="mt-3 text-slate-500">No courses available right now.</div>
              </div>
            ) : (
              <div className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {courses.map((c) => {
                  const isEnrolled = enrolledIds.has(c.id);
                  return (
                    <div key={c.id} className="p-5 rounded-2xl border border-slate-200 bg-white hover:border-indigo-300 hover:shadow-sm transition" data-testid={`course-card-${c.id}`}>
                      {c.thumbnail_url && <img src={c.thumbnail_url} alt={c.title} className="w-full h-32 object-cover rounded-lg mb-3" />}
                      <div className="text-[11px] uppercase tracking-[0.2em] font-bold flex items-center gap-2">
                        <span className="text-indigo-600">{c.level || "Beginner"}</span>
                        {c.duration && <span className="text-slate-500">· {c.duration}</span>}
                        {c.is_free
                          ? <span className="text-emerald-600 ml-auto">FREE</span>
                          : <span className="text-amber-600 ml-auto">₹{c.price}</span>}
                      </div>
                      <h3 className="heading text-lg font-bold mt-2">{c.title}</h3>
                      <p className="mt-1 text-sm text-slate-600 line-clamp-3">{c.description}</p>
                      <div className="mt-2 text-xs text-slate-500">{(c.lessons || []).length} lessons</div>
                      {isEnrolled ? (
                        <Link to={`/student/courses/${c.id}`} className="mt-4 inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-full px-5 py-2 text-sm" data-testid={`open-course-${c.id}`}>
                          <PlayCircle size={14} /> Open Course
                        </Link>
                      ) : c.is_free ? (
                        <button onClick={() => enrollFree(c.id)} disabled={enrolling === c.id} className="mt-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-semibold rounded-full px-5 py-2 text-sm" data-testid={`enroll-free-${c.id}`}>
                          {enrolling === c.id ? "Enrolling..." : "Enroll (Free)"}
                        </button>
                      ) : (
                        <button onClick={() => payAndEnrol(c.id)} disabled={enrolling === c.id} className="mt-4 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-semibold rounded-full px-5 py-2 text-sm" data-testid={`enroll-paid-${c.id}`}>
                          {enrolling === c.id ? "Processing..." : `Enroll · ₹${c.price}`}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
